// ═══════════════════════════════════════════════════════════════════════════
// Centralised enum values — single source of truth for valid status / role /
// type strings across the codebase.
//
// MIRRORS the database ENUM types and CHECK constraints created in
// docs/enum-migration.sql. Any change here MUST be reflected by an
// ALTER TYPE … ADD VALUE migration on the database (and vice versa) so
// frontend, Edge Functions, and Postgres stay in sync.
//
// Database mirror:
//   ROLES                 → profiles.role CHECK constraint (chk_profiles_role)
//   LOGIN_METHODS         → profiles.login_method CHECK (chk_profiles_login_method)
//   REQUEST_STATUSES      → request_status_enum
//   APPROVAL_STATUSES     → approval_status_enum
//   FEE_TYPES             → fee_type_enum
//   DEVICE_TYPES          → device_type_enum
//
// USAGE PATTERN
//   import { ROLES, isStaff } from '../utils/enums';
//   if (user.role === ROLES.ADMIN) { ... }
//   if (isStaff(user.role)) { ... }
//
// Do NOT inline magic strings like `if (role === 'admin')` in new code.
// Use the named constants. This makes role/status renames into a single-edit
// operation instead of a multi-language grep.
// ═══════════════════════════════════════════════════════════════════════════

// ── User roles ───────────────────────────────────────────────────────────
// Mirrors the chk_profiles_role CHECK constraint on the profiles table.
// SUPERUSER (added 7 July 2026) is the top internal tier: full access + the only
// role that can edit the per-admin permissions matrix. ADMIN is the reduced
// "Supervisor" tier whose module access is stored per-account (see
// docs/superuser-role-plan.md + src/app/access.ts). The DB CHECK must allow
// 'superuser' (Slice 0) before any account can hold it.
export const ROLES = {
  ADMIN: "admin",
  SUPERUSER: "superuser",
  STAFF: "staff",
  PICKER: "picker",
  SHELTER: "shelter",
  COMBINED: "combined",
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const ROLE_VALUES = Object.values(ROLES) as Role[];
export const isValidRole = (r: unknown): r is Role => ROLE_VALUES.includes(r as Role);

// Common role-set checks — use these instead of inline arrays in permission gates.
export const STAFF_ROLES = [ROLES.STAFF, ROLES.ADMIN, ROLES.COMBINED] as const;
export const ADMIN_ROLES = [ROLES.ADMIN, ROLES.COMBINED] as const;
export const isStaff = (role: string | null | undefined): boolean =>
  STAFF_ROLES.includes(role as (typeof STAFF_ROLES)[number]);
export const isAdmin = (role: string | null | undefined): boolean =>
  ADMIN_ROLES.includes(role as (typeof ADMIN_ROLES)[number]);

// ── Staff login method (Google SSO) ──────────────────────────────────────
// Mirrors the chk_profiles_login_method CHECK on profiles.login_method
// (docs/db-25-profiles-login-method.sql). Per-staff-member choice of how they
// sign in; shelter users are always PASSWORD. See docs/google-sso-plan.md.
export const LOGIN_METHODS = {
  PASSWORD: "password",
  GOOGLE: "google",
  EITHER: "either",
} as const;

export type LoginMethod = (typeof LOGIN_METHODS)[keyof typeof LOGIN_METHODS];

export const LOGIN_METHOD_VALUES = Object.values(LOGIN_METHODS) as LoginMethod[];
export const isValidLoginMethod = (m: unknown): m is LoginMethod =>
  LOGIN_METHOD_VALUES.includes(m as LoginMethod);

// ── Request workflow status ──────────────────────────────────────────────
// The COMPLETE set of request-level statuses (26 June 2026). The overall status
// uses a "Processed" vocabulary only — the words "Approval"/"Approved"/"Declined"
// are item-level (per-item approval) and NEVER appear at the request level.
//   Draft → Submitted → Part Processed → Fully Processed
//     • Part Processed  = some items decided, at least one still undecided
//     • Fully Processed = every item has a final Approve/Decline
// The old request-level values "Partial approval" / "Approved" / "Declined" /
// "Completed" / "Cancelled" were RETIRED here. They still exist in the Postgres
// `request_status_enum` as inert ghosts until Part B (the enum recreation,
// bundled with the Slice 2 db-8 work) drops them — see docs/status-model.md.
export const REQUEST_STATUSES = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  PART_PROCESSED: "Part Processed",
  FULLY_PROCESSED: "Fully Processed",
} as const;

export type RequestStatus = (typeof REQUEST_STATUSES)[keyof typeof REQUEST_STATUSES];

export const REQUEST_STATUS_VALUES = Object.values(REQUEST_STATUSES) as RequestStatus[];
export const isValidRequestStatus = (s: unknown): s is RequestStatus =>
  REQUEST_STATUS_VALUES.includes(s as RequestStatus);

// Convenience: statuses that mean the request is still in flight (not finalised).
// FULLY_PROCESSED is terminal, so it is deliberately excluded. (The old APPROVED
// entry was dropped here when all-approved collapsed into FULLY_PROCESSED — D-1.)
export const ACTIVE_REQUEST_STATUSES = [
  REQUEST_STATUSES.SUBMITTED,
  REQUEST_STATUSES.PART_PROCESSED,
] as const;

// ── Per-item approval status ─────────────────────────────────────────────
// Mirrors approval_status_enum. Used by children_fees, children_uniforms,
// children_shoes, children_devices.
export const APPROVAL_STATUSES = {
  PENDING: "Pending",
  APPROVED: "Approved",
  DECLINED: "Declined",
  NEEDS_INFO: "Needs Info",
} as const;

export type ApprovalStatus = (typeof APPROVAL_STATUSES)[keyof typeof APPROVAL_STATUSES];

export const APPROVAL_STATUS_VALUES = Object.values(APPROVAL_STATUSES) as ApprovalStatus[];
export const isValidApprovalStatus = (s: unknown): s is ApprovalStatus =>
  APPROVAL_STATUS_VALUES.includes(s as ApprovalStatus);

// ── Fee categories ───────────────────────────────────────────────────────
// Mirrors fee_type_enum. Stationery & Textbooks split between non-QLD ("&"
// combined) and QLD ("Stationery" / "Textbooks" separate) because Education
// Queensland recognises them as separate billable items.
export const FEE_TYPES = {
  STATIONERY_AND_TEXTBOOKS: "Stationery & Textbooks",
  STATIONERY: "Stationery",
  TEXTBOOKS: "Textbooks",
  CAMP_FEES: "Camp Fees",
  RESOURCE_FEES: "Resource Fees",
  DEVICE_HIRE: "Device Hire",
  SCHOOL_ACTIVITIES: "School Activities",
  NON_SCHOOL_ACTIVITIES: "Non-school Activities",
  SWIM_GEAR: "Swim Gear",
  OTHER: "Other (specify)",
} as const;

export type FeeType = (typeof FEE_TYPES)[keyof typeof FEE_TYPES];

export const FEE_TYPE_VALUES = Object.values(FEE_TYPES) as FeeType[];
export const isValidFeeType = (t: unknown): t is FeeType => FEE_TYPE_VALUES.includes(t as FeeType);

// ── Device types ─────────────────────────────────────────────────────────
// Mirrors device_type_enum.
export const DEVICE_TYPES = {
  LAPTOP: "Laptop",
  IPAD: "iPad",
} as const;

export type DeviceType = (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES];

export const DEVICE_TYPE_VALUES = Object.values(DEVICE_TYPES) as DeviceType[];
export const isValidDeviceType = (t: unknown): t is DeviceType =>
  DEVICE_TYPE_VALUES.includes(t as DeviceType);
